import { calendarSelectedItemIdState } from '@atom/calendar/calendar';
import Icon from '@components/dataDisplay/Icons/Icon';
import { useReadPermission } from '@hooks/usePermission';
import { ACCESS_TYPE } from '@typings/common/accessType';
import { IcalendarItem } from '@typings/calendar/calendar.interface';
import React, { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import styled from 'styled-components';
import Tooltip from '@components/dataDisplay/Tooltip';

const getBorderRadius = (pos: 'single' | 'start' | 'middle' | 'end') => {
  switch (pos) {
    case 'single':
      return '6px';
    case 'start':
      return '6px 0 0 6px';
    case 'middle':
      return '0';
    case 'end':
      return '0 6px 6px 0';
    default:
      return '6px';
  }
};

const getConnectionStyles = (pos: 'single' | 'start' | 'middle' | 'end') => {
  if (pos === 'start' || pos === 'middle') {
    return `
      &::after {
        content: '';
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        width: 1px;
        background: inherit;
        z-index: 1;
      }
    `;
  }
  return '';
};

const ItemWrap = styled.div<{ $pos: 'single' | 'start' | 'middle' | 'end' }>`
  padding: 2px 3px;
  border-radius: ${({ $pos }) => getBorderRadius($pos)};
  font-size: 10px;
  color: #fff;
  cursor: var(--hoverCursorURL), pointer;
  position: relative;

  ${({ $pos }) => getConnectionStyles($pos)}

  img {
    width: 100%;
  }
`;

const TitleText = styled.span<{ $hidden: boolean; $bg: string }>`
  color: ${({ $hidden, $bg }) => ($hidden ? $bg : '#fff')};
`;

const MSG = styled.div`
  font-size: 11px;
  white-space: normal;
  word-break: break-word;
  line-height: 1.5;
`;

const TooltipImg = styled.img`
  width: 220px;
  max-width: 220px;
  max-height: 140px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
  margin-bottom: 6px;
`;

const parseHtmlContent = (html: string, max = 1000): { text: string; imgSrc: string | null } => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const img = doc.querySelector('img');
  const imgSrc = img ? img.getAttribute('src') : null;
  const text = (doc.body.textContent || '').trim();
  const truncated = text.length > max ? text.slice(0, max) + '...' : text;
  return { text: truncated, imgSrc };
};

export const AccessItemMsgMap = {
  [ACCESS_TYPE.SECRET]: () => <Icon name="Lock14" />,
  [ACCESS_TYPE.MEMBER]: () => <Icon name="Lock14" />,
  [ACCESS_TYPE.PUBLIC]: (data) => {
    const { text, imgSrc } = parseHtmlContent(data.contents || '');
    const resolvedImgSrc = data.imgURL || imgSrc;
    return (
      <div>
        {resolvedImgSrc && <TooltipImg src={resolvedImgSrc} alt="" />}
        {text && <MSG>{text}</MSG>}
      </div>
    );
  },
};

type Props = {
  data: IcalendarItem;
  rangePosition?: 'single' | 'start' | 'middle' | 'end';
};
function CalendrItem({ data, rangePosition = 'single' }: Props) {
  const setSelectedId = useSetRecoilState(calendarSelectedItemIdState);

  const { id, title, authorUid, accessType, primaryColor, imgURL } = data;
  const handleClickItem = useCallback((id) => {
    setSelectedId(id);
  }, []);

  const readPermission = useReadPermission({
    accessType: accessType || ACCESS_TYPE.PUBLIC,
    authorUid: authorUid || '',
  });

  const isStartOrSingle =
    rangePosition === 'single' || rangePosition === 'start';

  return (
    <Tooltip
      message={AccessItemMsgMap[accessType](data)}
      direction="right"
      isPortal
      maxWidth="240px"
    >
      <ItemWrap
        key={id}
        style={{ backgroundColor: primaryColor }}
        onClick={() => handleClickItem(id)}
        $pos={rangePosition}
      >
        <TitleText $hidden={!isStartOrSingle} $bg={primaryColor}>
          {readPermission ? title : ''}
        </TitleText>
      </ItemWrap>
    </Tooltip>
  );
}

export default CalendrItem;
